/**
 * Real-time collaboration client for LibreDraw.
 *
 * Talks to the `CollabSync` WebSocket walker (jac-scale broadcast endpoint) at
 * `ws://<host>/ws/CollabSync?token=<jwt>`. Every message a client sends is
 * broadcast to ALL open connections, so this client tags each message with the
 * session `shareId` + a per-tab `sender` id and, on receipt, keeps only the
 * messages for its own share that came from someone else.
 *
 * Message kinds:
 *   - "doc"      full element list of a page  { pageId, elements }
 *   - "cursor"   live pointer presence         { x, y }
 *   - "presence" join / leave heartbeat        { state: "join"|"leave" }
 *   - "hello"    a late joiner asking peers to re-broadcast their doc
 */

export type CollabKind = "doc" | "cursor" | "presence" | "hello" | "comment";

export interface CollabMessage {
  share_id: string;
  kind: CollabKind;
  sender: string;
  name: string;
  payload: any;
}

interface CollabOptions {
  shareId: string;
  token: string;
  selfId: string;
  selfName: string;
  onMessage: (m: CollabMessage) => void;
  onStatus?: (connected: boolean) => void;
}

export class CollabClient {
  private ws: WebSocket | null = null;
  private opts: CollabOptions;
  private closed = false;
  private backoff = 500;
  private pending: string[] = [];

  constructor(opts: CollabOptions) {
    this.opts = opts;
  }

  connect() {
    if (this.closed) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws/CollabSync?token=${encodeURIComponent(this.opts.token)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = 500;
      this.opts.onStatus?.(true);
      // flush anything queued while offline, then announce ourselves
      const q = this.pending.splice(0);
      for (const m of q) ws.send(m);
      this.send("presence", { state: "join" });
      this.send("hello", {});
    };

    ws.onmessage = (ev) => {
      let parsed: any;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }
      // the broadcast wraps the walker report: { ok, data: { reports: [ <our report> ] } }
      const rep = parsed?.data?.reports?.[0];
      if (!rep || rep.share_id !== this.opts.shareId) return;
      if (rep.sender === this.opts.selfId) return; // ignore our own echo
      this.opts.onMessage(rep as CollabMessage);
    };

    ws.onclose = () => {
      this.opts.onStatus?.(false);
      if (!this.closed) this.scheduleReconnect();
    };
    ws.onerror = () => {
      try { ws.close(); } catch { /* noop */ }
    };
  }

  private scheduleReconnect() {
    if (this.closed) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, 8000);
    setTimeout(() => this.connect(), delay);
  }

  send(kind: CollabKind, payload: any) {
    const msg = JSON.stringify({
      share_id: this.opts.shareId,
      kind,
      sender: this.opts.selfId,
      name: this.opts.selfName,
      payload,
      token: this.opts.token,
    });
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else if (kind === "doc" || kind === "presence") {
      // keep only the latest doc so we don't flush a stale backlog on reconnect
      if (kind === "doc") this.pending = this.pending.filter((m) => !m.includes('"kind":"doc"'));
      this.pending.push(msg);
    }
  }

  close() {
    this.closed = true;
    try {
      this.send("presence", { state: "leave" });
    } catch { /* noop */ }
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = null;
  }
}

/** Factory that constructs the client on the TS side. cl has a `new(Cls, ...)` builtin,
 *  but it needs a checker-resolvable class type - an imported .tsx class reads as
 *  `Unknown` to the cl checker, so a typed factory here is the clean construction
 *  boundary. (A plain global like URLSearchParams IS resolvable, so callers use
 *  `new(URLSearchParams, ...)` directly.) */
export function createCollab(opts: CollabOptions): CollabClient {
  return new CollabClient(opts);
}

/** The caller's own identity values (username + email) from /user/me - used to
 *  match against a shared doc's invite list. Trustworthy for the caller's OWN
 *  identity (it's their token). Empty for anonymous/no-token. */
export async function myIdentities(token: string): Promise<string[]> {
  if (!token) return [];
  try {
    const r = await fetch(location.origin + "/user/me", { headers: { Authorization: "Bearer " + token } });
    const d = await r.json();
    return ((d?.data?.identities || []) as any[]).map((i) => i.value).filter(Boolean);
  } catch {
    return [];
  }
}

/** Register an account with a username AND (optionally) an email identity, so the
 *  user is invitable by email. Returns true on success (then log in normally). */
export async function registerAccount(username: string, password: string, email: string): Promise<boolean> {
  const identities: any[] = [{ type: "username", value: username }];
  if (email) identities.push({ type: "email", value: email });
  try {
    const r = await fetch(location.origin + "/user/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identities, credential: { type: "password", password } }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/** Promote the current device-guest into a real account IN PLACE: attach an email
 *  identity and set a real password on the *same* user, so the root never changes and
 *  the guest's diagrams carry over. `guestPassword` is the guest's stored random
 *  password (required as `current_password`). The current token stays valid (Jac
 *  doesn't revoke on password change). Returns true on success. */
export async function promoteGuest(email: string, password: string, guestPassword: string): Promise<boolean> {
  const token = localStorage.getItem("jac_token") || "";
  if (!token) return false;
  const auth = { "Content-Type": "application/json", Authorization: "Bearer " + token };
  try {
    const r1 = await fetch(location.origin + "/user/add-identity", {
      method: "POST",
      headers: auth,
      body: JSON.stringify({ identity: { type: "email", value: email } }),
    });
    if (!r1.ok) return false;
    const r2 = await fetch(location.origin + "/user/password", {
      method: "PUT",
      headers: auth,
      body: JSON.stringify({ current_password: guestPassword, new_password: password }),
    });
    return r2.ok;
  } catch {
    return false;
  }
}

/** A stable, readable identity for this browser tab's collaborator. */
export function makeCollaborator(): { id: string; name: string; color: string } {
  const colors = ["#4f46e5", "#0ea5e9", "#059669", "#dc2626", "#d97706", "#7c3aed", "#db2777", "#0891b2"];
  const rid = Math.random().toString(36).slice(2, 8);
  const animals = ["Fox", "Owl", "Bee", "Elk", "Jay", "Ram", "Cub", "Kit", "Doe", "Hare"];
  const name = "Guest " + animals[Math.floor(Math.random() * animals.length)];
  const color = colors[Math.floor(Math.random() * colors.length)];
  return { id: rid, name, color };
}
